<template>
  <label class="ui-field" ref="root">
    <span v-if="label">{{ label }}</span>
    <div class="ui-select" :class="{ open }" tabindex="0" @click="toggle" @keydown="onKeydown" @blur="close">
      <span class="ui-select-value" :class="{ 'is-placeholder': isPlaceholder }">{{ selectedText }}</span>
      <svg class="ui-select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <ul v-if="open" class="ui-select-menu" role="listbox">
        <li
          v-for="item in normalizedItems"
          :key="String(item.value)"
          class="ui-select-option"
          :class="{ selected: String(item.value) === String(value) }"
          role="option"
          @mousedown.prevent="select(item)"
        >
          {{ item.text }}
        </li>
      </ul>
    </div>
  </label>
</template>

<script>
export default {
  name: 'UiSelect',
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      open: false
    }
  },
  computed: {
    normalizedItems () {
      return this.items.map((item) => {
        if (item && typeof item === 'object') {
          return {
            text: item.text || item.label || item.name || String(item.value),
            value: item.value
          }
        }
        return { text: String(item), value: item }
      })
    },
    selectedText () {
      if (this.placeholder && String(this.value) === 'all') return this.placeholder
      const match = this.normalizedItems.find((item) => String(item.value) === String(this.value))
      return match ? match.text : ''
    },
    isPlaceholder () {
      return Boolean(this.placeholder) && String(this.value) === 'all'
    }
  },
  methods: {
    toggle () {
      this.open = !this.open
    },
    close () {
      this.open = false
    },
    select (item) {
      this.open = false
      this.$emit('input', item.value)
      this.$emit('change', item.value)
    },
    onKeydown (event) {
      if (event.key === 'Escape') {
        this.close()
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.toggle()
      }
    }
  }
}
</script>

<style scoped>
.ui-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.ui-field span {
  color: hsl(var(--muted-foreground));
  font-size: 0.72rem;
  font-weight: 700;
}

.ui-select {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  min-width: 0;
  padding: 0 32px 0 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  outline: none;
  background: hsl(var(--input));
  color: hsl(var(--foreground));
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}

.ui-select:hover {
  border-color: hsl(var(--ring) / 0.6);
}

.ui-select:focus,
.ui-select.open {
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 3px hsl(var(--ring) / 0.18);
}

.ui-select-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-select-value.is-placeholder {
  color: hsl(var(--muted-foreground));
}

.ui-select-caret {
  position: absolute;
  right: 12px;
  color: hsl(var(--muted-foreground));
  transition: transform 140ms ease;
  pointer-events: none;
}

.ui-select.open .ui-select-caret {
  transform: rotate(180deg);
}

.ui-select-menu {
  position: absolute;
  z-index: 40;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 260px;
  margin: 0;
  padding: 6px;
  list-style: none;
  overflow-y: auto;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--popover));
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.36);
}

.ui-select-option {
  padding: 8px 10px;
  border-radius: 8px;
  color: hsl(var(--popover-foreground));
  font-size: 0.86rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.ui-select-option:hover {
  background: hsl(var(--accent-token) / 0.6);
}

.ui-select-option.selected {
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  font-weight: 600;
}
</style>
